import React from "react";

export const FILLER_TEXT = '\u00A0';

const DEFAULT_MAX_FONT_CQI = '100cqi';

// .qty/.unit inherit body's font-family/weight (see dashboard.scss + _vars.scss $system-font-family)
const MEASURE_FONT_SIZE = 100; // px, arbitrary reference size \u2014 text width scales linearly with font-size
const STRIP_FONT_FAMILY = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif';
const STRIP_FONT_WEIGHT = 400;

let measureCanvas;
const measureTextWidth = (text, font) => {
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(text).width;
}

// canvas measureText() is only an approximation of actual DOM text rasterization (kerning/hinting/
// subpixel rounding can differ slightly), so a literal 0-margin fit occasionally wraps — pad the
// measured width to leave headroom
const SAFETY_MARGIN = 1.2;

// Greedy word-wrap simulation: how many lines are needed to fit `wordWidths` (each separated by
// `spaceWidth`) into a line budget of `lineWidth`, all in the same measurement units
const countWrappedLines = (wordWidths, spaceWidth, lineWidth) => {
    let lines = 1;
    let current = 0;
    for (const width of wordWidths) {
        const next = current === 0 ? width : current + spaceWidth + width;
        if (next > lineWidth) {
            lines += 1;
            current = width;
        } else {
            current = next;
        }
    }
    return lines;
}

const BINARY_SEARCH_ITERATIONS = 30;

// Binary-search the max font-size-to-container-width ratio for which the string, greedy
// word-wrapped, needs no more than `maxLines` lines — dividing total width evenly by line count
// overestimates the fit, since a word that doesn't fit at the end of a line forces an extra wrap
// even when the total text width would theoretically allow one fewer line.
const calculateCqi = (str, maxLines = 1, uppercase = false) => {
    const text = uppercase ? str.toUpperCase() : str;
    const font = `${STRIP_FONT_WEIGHT} ${MEASURE_FONT_SIZE}px ${STRIP_FONT_FAMILY}`;
    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) return DEFAULT_MAX_FONT_CQI;

    const spaceWidth = measureTextWidth(' ', font) * SAFETY_MARGIN;
    const wordWidths = words.map(w => measureTextWidth(w, font) * SAFETY_MARGIN);
    const widestWord = Math.max(...wordWidths);

    // At ratio `r` (fontSize / containerWidth), a piece measured `widthAtRef` at MEASURE_FONT_SIZE
    // renders at `widthAtRef * r / MEASURE_FONT_SIZE` relative to the container's full width (=1)
    const fitsWithinLines = (r) => countWrappedLines(
        wordWidths.map(w => w * r / MEASURE_FONT_SIZE),
        spaceWidth * r / MEASURE_FONT_SIZE,
        1
    ) <= maxLines;

    let low = 0;
    let high = MEASURE_FONT_SIZE / widestWord; // r above this can't even fit the widest word on its own line
    for (let i = 0; i < BINARY_SEARCH_ITERATIONS; i++) {
        const mid = (low + high) / 2;
        if (fitsWithinLines(mid)) low = mid; else high = mid;
    }
    return `${low * 100}cqi`;
}

// multiLineAttrs: keys allowed to wrap onto 2 lines instead of requiring a single-line fit
// uppercaseAttrs: keys rendered with CSS text-transform: uppercase, so the wider uppercased glyphs must be measured instead of the raw string
export const calculatePresentAttributes = (allAttrs, vars, setPresentAttributes, multiLineAttrs = [], uppercaseAttrs = []) => {
    const newPresentAttributes = allAttrs.reduce((acc, key) => {
        const values = vars
            .map(obj => obj[key])
            .filter(v => v !== undefined && v !== null)
            .map(v => String(v));

        const longestStr = values.reduce((longest, curr) =>
            curr.length > longest.length ? curr : longest,
            ""
        );
        if (longestStr.length != ''){
            acc[key] = [longestStr, calculateCqi(longestStr, multiLineAttrs.includes(key) ? 2 : 1, uppercaseAttrs.includes(key))];
        }
        return acc;
    }, {});
    setPresentAttributes(newPresentAttributes);
}

// Render the longest string for every datastrip so to have same container
// Make those string invisible but keep its size by having color to background
// Finally, render the actual string as absolute on top
function StripFormatter({presentAttributes, classNames = '', fontVar, str, attrKey, color, defaultFont}) {
    const currentAttr = presentAttributes?.[attrKey] || [FILLER_TEXT, DEFAULT_MAX_FONT_CQI]
    return (
        <p className={classNames} style={{
            display: 'flex', 
            flexDirection: 'column',
            "--font-size": `${fontVar || defaultFont}px`,
            "--max-font-size": `${currentAttr[1]}`}}
        >
            <span style={{position: 'absolute', width: '100%'}}>{str}</span>
            <span style={{color: color}}>{currentAttr[0]}</span>
        </p>
    )
}

export default StripFormatter;
